import { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { getUserMe, PROFILE_UPDATED_EVENT, UserProfileDto, waitForAuthUser } from './secureFunctions';

let cachedPhoto: string | null = null;
let cachedFor: string | null = null;
let pending: Promise<string> | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener(PROFILE_UPDATED_EVENT, (e) => {
    const p = (e as CustomEvent<UserProfileDto>).detail;
    if (p && 'photoURL' in p) {
      cachedPhoto = p.photoURL || '';
      cachedFor = auth.currentUser?.uid || cachedFor;
    }
  });
}

/** The signed-in user's saved profile photo (falls back to the login photo). */
export async function getMyPhoto(): Promise<string> {
  const user = auth.currentUser || await waitForAuthUser();
  if (!user) return '';
  if (cachedFor === user.uid && cachedPhoto !== null) return cachedPhoto;
  if (!pending) {
    pending = getUserMe()
      .then((me) => me.profile?.photoURL || user.photoURL || '')
      .catch(() => user.photoURL || '')
      .then((photo) => {
        cachedPhoto = photo;
        cachedFor = user.uid;
        return photo;
      })
      .finally(() => { pending = null; });
  }
  return pending;
}

/** React hook that stays in sync when the user changes their profile photo. */
export function useMyPhoto(): string {
  const [photo, setPhoto] = useState<string>(() =>
    cachedFor && cachedFor === auth.currentUser?.uid && cachedPhoto !== null ? cachedPhoto : '');
  useEffect(() => {
    let alive = true;
    getMyPhoto().then((p) => { if (alive) setPhoto(p); });
    const onUpdate = (e: Event) => {
      const p = (e as CustomEvent<UserProfileDto>).detail;
      if (p && 'photoURL' in p) setPhoto(p.photoURL || '');
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onUpdate);
    return () => { alive = false; window.removeEventListener(PROFILE_UPDATED_EVENT, onUpdate); };
  }, []);
  return photo;
}
